import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ICertMasterPlatformAccessKeyRedemption,
  CertMasterPlatformRedemptionStatus,
  ICertMasterPlatformUserProduct,
  IProfile,
  IError,
} from '@comptia-sso/core';

import {
  useCertMasterPlatformRedeemAccessKey,
  useCertMasterPlatformEmailExists,
  useCertMasterPlatformAccessKeyValid,
  useProfile,
} from 'hooks';
import { useCertMasterPlatformAccessKeyCECheck } from 'hooks/entitlements/useGetCertMasterPlatformAccessKeyCECheck';

export type RedeemAccessKeyResult = {
  errorMessage?: string;
  success: boolean;
};

type CertMasterPlatformRedemptionContextType = {
  redeemAccessKey: (
    accessKey: string,
    checkCertMasterAccount?: boolean,
  ) => Promise<RedeemAccessKeyResult>;
  hasProductAlready: boolean;
  hasPossibleCertMasterAccount: boolean;
  needToLinkToCertMaster: boolean;
  activatedProducts: ICertMasterPlatformUserProduct[];
  accessKey?: string;
};

const CertMasterPlatformRedemptionContext = createContext<
  CertMasterPlatformRedemptionContextType | undefined
>(undefined);

export function CertMasterPlatformRedemptionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [profile] = useProfile();
  const { t } = useTranslation();
  const [redeemCertMasterPlatformAccessKey] =
    useCertMasterPlatformRedeemAccessKey(profile?.id);
  const [getCertMasterPlatformEmailExists] = useCertMasterPlatformEmailExists();
  const [getCertMasterPlatformAccessKeyValid] =
    useCertMasterPlatformAccessKeyValid();
  const [getCertMasterPlatformAccessKeyCECheck] =
    useCertMasterPlatformAccessKeyCECheck();
  const [activatedProducts, setActivatedProducts] = useState<
    ICertMasterPlatformUserProduct[]
  >([]);
  const [hasProductAlready, setHasProductAlready] = useState(false);
  const [hasPossibleCertMasterAccount, setHasPossibleCertMasterAccount] =
    useState(false);
  const [needToLinkToCertMaster, setNeedToLinkToCertMaster] = useState(false);
  const [accessKey, setAccessKey] = useState<string | undefined>();

  const reset = useCallback(() => {
    setActivatedProducts([]);
    setHasProductAlready(false);
    setHasPossibleCertMasterAccount(false);
    setNeedToLinkToCertMaster(false);
    setAccessKey(undefined);
  }, []);

  const redeemAccessKey = useCallback(
    async (
      accessKey: string,
      checkCertMasterAccount = true,
    ): Promise<RedeemAccessKeyResult> => {
      reset();
      setAccessKey(accessKey);
      let result: ICertMasterPlatformAccessKeyRedemption;
      let isAccessKeyValid = false;

      // Is the access key valid?
      try {
        isAccessKeyValid =
          (await getCertMasterPlatformAccessKeyValid(accessKey))?.isValid ===
          true;
      } catch {
        return { success: false };
      }

      if (!isAccessKeyValid) {
        return { success: false };
      }

      // CE Check
      try {
        await getCertMasterPlatformAccessKeyCECheck({
          key: accessKey,
          userId: profile?.id,
        });
      } catch (error) {
        return { errorMessage: (error as IError)?.message, success: false };
      }

      if (!hasCertMasterPlatformAccount(profile) && checkCertMasterAccount) {
        const result = await getCertMasterPlatformEmailExists(profile.email);
        setHasPossibleCertMasterAccount(result.doesExist);
        setNeedToLinkToCertMaster(true);

        return { success: true };
      }

      try {
        result = await redeemCertMasterPlatformAccessKey(accessKey);
      } catch (errorMessage) {
        return {
          errorMessage: (errorMessage as IError)?.message,
          success: false,
        };
      }

      setActivatedProducts(result.activatedProducts ?? []);

      if (
        result.redemptionStatus ===
        CertMasterPlatformRedemptionStatus.HasProductAlready
      ) {
        setHasProductAlready(true);
        return { success: false };
      }

      if (
        result.redemptionStatus !== CertMasterPlatformRedemptionStatus.Success
      ) {
        const errorMessage = t(
          `RedeemModule.CertMasterRedemtionStatus.${
            CertMasterPlatformRedemptionStatus[
              result.redemptionStatus ?? 'UnknownError'
            ]
          }`,
        );
        console.error(errorMessage);

        return { errorMessage, success: false };
      }

      return { success: true };
    },
    [
      profile,
      getCertMasterPlatformAccessKeyValid,
      getCertMasterPlatformEmailExists,
      redeemCertMasterPlatformAccessKey,
      t,
      reset,
    ],
  );

  const contextValue = useMemo(
    () => ({
      redeemAccessKey,
      needToLinkToCertMaster,
      hasProductAlready,
      hasPossibleCertMasterAccount,
      activatedProducts,
      accessKey,
    }),
    [
      redeemAccessKey,
      needToLinkToCertMaster,
      hasProductAlready,
      hasPossibleCertMasterAccount,
      activatedProducts,
      accessKey,
    ],
  );

  return (
    <CertMasterPlatformRedemptionContext.Provider value={contextValue}>
      {children}
    </CertMasterPlatformRedemptionContext.Provider>
  );
}

function hasCertMasterPlatformAccount(profile: IProfile) {
  return profile.externalIds.certMaster != null;
}

export function useCertMasterPlatformRedemption(): CertMasterPlatformRedemptionContextType {
  const context = useContext(CertMasterPlatformRedemptionContext);

  if (!context)
    throw new Error(
      'useCertMasterPlatformRedemption must be used within a CertMasterPlatformRedemptionProvider',
    );

  return context;
}
